import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Landmark } from "lucide-react";

/**
 * BondVsHouse — one stage, two models.
 *
 * A single diagram that morphs between Peer-to-peer (BetBond) and Sportsbook
 * (typical operator). Same two people, same money — only the line changes.
 *
 *   Peer-to-peer:  You ──Bond arc── Match
 *                       \         /
 *                        Escrow · 7% disclosed
 *
 *   Sportsbook:    You ───dashed line───→ House
 *                       Margin · hidden
 *
 * Auto-flips once after a short delay to demonstrate the difference, then
 * waits for the user. Once they tap the toggle, the auto-flip stops.
 */

const ACCENT = "#1E88E5";

const MODES = {
  PEER: "peer",
  HOUSE: "house",
};

const CAPTIONS = {
  [MODES.PEER]:
    "BetBond is the platform layer: two users agree, funds sit in escrow, and the 7% platform fee is shown before confirmation.",
  [MODES.HOUSE]:
    "A sportsbook is different: the operator becomes the counterparty. The margin is folded into the odds instead of shown as a clear platform fee.",
};

// Structured contrast shown beneath the diagram. Swaps with the toggle so both
// modes read as full, concrete comparisons instead of an empty stage.
const COMPARE = {
  [MODES.PEER]: [
    { k: "Counterparty", v: "Another real person" },
    { k: "The cut", v: "7% fee, shown upfront" },
    { k: "If you win", v: "Their stake, minus 7%" },
  ],
  [MODES.HOUSE]: [
    { k: "Counterparty", v: "The operator itself" },
    { k: "The cut", v: "Margin baked into odds" },
    { k: "If you win", v: "Paid at trimmed odds" },
  ],
};

const Toggle = ({ mode, onChange }) => {
  return (
    <div className="inline-flex items-center p-1 rounded-full border border-white/10 bg-black/55 shadow-[0_18px_60px_-34px_rgba(30,136,229,0.85)] backdrop-blur-sm">
      {[
        { id: MODES.PEER, label: "BetBond" },
        { id: MODES.HOUSE, label: "Sportsbook" },
      ].map((opt) => {
        const active = mode === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`relative px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors duration-300 ${
              active ? "text-white" : "text-gray-500 hover:text-gray-300"
            }`}
            aria-pressed={active}
          >
            {active && (
              <motion.span
                layoutId="toggle-bg"
                className="absolute inset-0 rounded-full bg-gradient-to-b from-[#42A5F5] to-[#1565C0]"
                style={{
                  boxShadow: "0 6px 22px -8px rgba(30,136,229,0.8)",
                }}
                transition={{ type: "spring", stiffness: 360, damping: 30 }}
              />
            )}
            <span className="relative">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const PeerNode = ({ label, sub, dim }) => (
  <motion.div
    animate={{ opacity: dim ? 0.25 : 1, scale: dim ? 0.94 : 1 }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-center"
  >
    <div
      className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-white/[0.10] to-white/[0.02] flex items-center justify-center"
      style={{
        boxShadow: dim
          ? "0 0 0 1px rgba(255,255,255,0.06)"
          : `0 0 0 1px rgba(30,136,229,0.45), 0 0 28px rgba(30,136,229,0.25)`,
      }}
    >
      <span className="text-white font-bold text-base sm:text-lg select-none">
        {label.charAt(0)}
      </span>
      <span
        className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#1E88E5]"
        style={{ boxShadow: "0 0 0 2px #000" }}
      />
    </div>
    <div className="mt-2 text-center">
      <div className="text-white text-xs sm:text-sm font-semibold">{label}</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-gray-500 mt-0.5">
        {sub}
      </div>
    </div>
  </motion.div>
);

const HouseNode = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.92 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.92 }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-center"
  >
    <div
      className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.01] flex items-center justify-center"
      style={{
        boxShadow: "0 0 0 1px rgba(255,255,255,0.10)",
      }}
    >
      <Landmark size={26} className="text-gray-300" strokeWidth={1.7} />
    </div>
    <div className="mt-2 text-center">
      <div className="text-gray-300 text-xs sm:text-sm font-semibold">
        The House
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-gray-500 mt-0.5">
        Operator
      </div>
    </div>
  </motion.div>
);

const EscrowNode = ({ active }) => (
  <motion.div
    animate={{
      opacity: active ? 1 : 0.15,
      y: active ? 0 : 6,
      scale: active ? 1 : 0.94,
    }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-center"
  >
    <div
      className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[#1E88E5]/15 to-transparent flex items-center justify-center"
      style={{
        boxShadow: active
          ? "0 0 0 1px rgba(30,136,229,0.55), 0 0 30px rgba(30,136,229,0.3)"
          : "0 0 0 1px rgba(255,255,255,0.06)",
      }}
    >
      <Lock size={20} className="text-[#1E88E5]" strokeWidth={2.2} />
    </div>
    <div className="mt-2 text-center">
      <div className="text-white text-xs sm:text-sm font-semibold">Escrow</div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#1E88E5] mt-0.5">
        7% · disclosed
      </div>
    </div>
  </motion.div>
);

const BondVsHouse = () => {
  const [mode, setMode] = useState(MODES.PEER);
  const userInteractedRef = useRef(false);
  const autoToggleRef = useRef(null);

  // One automatic flip after ~3.5s so the comparison is visible without
  // requiring interaction. Cancelled the moment the user touches the toggle.
  useEffect(() => {
    autoToggleRef.current = setTimeout(() => {
      if (!userInteractedRef.current) setMode(MODES.HOUSE);
    }, 3500);
    return () => clearTimeout(autoToggleRef.current);
  }, []);

  const handleChange = (m) => {
    userInteractedRef.current = true;
    if (autoToggleRef.current) clearTimeout(autoToggleRef.current);
    setMode(m);
  };

  const isPeer = mode === MODES.PEER;

  return (
    <section className="relative overflow-hidden bg-black pt-20 pb-16 border-t border-white/5">
      <div
        className="absolute right-[-10%] top-10 h-80 w-80 rounded-full bg-[#1E88E5]/12 blur-[100px]"
        aria-hidden
      />
      <div
        className="absolute left-[-12%] bottom-0 h-72 w-72 rounded-full bg-[#64B5F6]/10 blur-[100px]"
        aria-hidden
      />
      <div className="container relative mx-auto px-4 max-w-4xl">
        {/* Heading */}
        <div className="text-center mb-6">
          <p className="mb-3 text-[10px] uppercase tracking-[0.26em] text-[#64B5F6]">
            Why P2P
          </p>
          <p className="mx-auto max-w-xl text-gray-500 text-sm leading-relaxed">
            On a P2P bond, BetBond is the platform — not the other side.
            Two people agree, escrow holds the stakes, and the 7% fee is shown
            before you confirm.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-6">
          <Toggle mode={mode} onChange={handleChange} />
        </div>

        {/* Stage */}
        <div
          className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.04] via-white/[0.018] to-transparent p-5 sm:p-8"
          style={{
            boxShadow:
              "0 34px 110px -52px rgba(30,136,229,0.9), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <div
            className="absolute left-1/2 top-0 h-44 w-3/4 -translate-x-1/2 bg-[#1E88E5]/10 blur-[80px]"
            aria-hidden
          />
          <div className="relative h-[230px] sm:h-[250px]">
            {/* Connecting lines — SVG layer (full canvas, percent-based) */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <defs>
                <linearGradient id="vh-arc" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity="0.0" />
                  <stop offset="50%" stopColor={ACCENT} stopOpacity="1" />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Peer bond arc */}
              <motion.path
                d="M 18 32 Q 50 4 82 32"
                fill="none"
                stroke="url(#vh-arc)"
                strokeWidth="0.7"
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                animate={{ opacity: isPeer ? 1 : 0 }}
                transition={{ duration: 0.45 }}
                style={{ filter: "drop-shadow(0 0 6px rgba(30,136,229,0.55))" }}
              />

              {/* Peer rails to escrow */}
              <motion.g
                animate={{ opacity: isPeer ? 0.55 : 0 }}
                transition={{ duration: 0.45 }}
              >
                <path
                  d="M 22 38 L 48 64"
                  stroke={ACCENT}
                  strokeWidth="0.4"
                  vectorEffect="non-scaling-stroke"
                  strokeDasharray="2 3"
                  fill="none"
                />
                <path
                  d="M 78 38 L 52 64"
                  stroke={ACCENT}
                  strokeWidth="0.4"
                  vectorEffect="non-scaling-stroke"
                  strokeDasharray="2 3"
                  fill="none"
                />
              </motion.g>

              {/* House direct line */}
              <motion.path
                d="M 22 36 L 78 36"
                fill="none"
                stroke="#6b7280"
                strokeWidth="0.6"
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeDasharray="4 4"
                animate={{ opacity: isPeer ? 0 : 1 }}
                transition={{ duration: 0.45 }}
              />
            </svg>

            {/* Nodes */}
            {/* You — left peer */}
            <div className="absolute left-[6%] top-[28%]">
              <PeerNode label="You" sub="Kigali" />
            </div>

            {/* Right node — peer or house */}
            <div className="absolute right-[6%] top-[28%]">
              <AnimatePresence mode="wait">
                {isPeer ? (
                  <motion.div
                    key="match"
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.92 }}
                    transition={{ duration: 0.4 }}
                  >
                    <PeerNode label="Counterparty" sub="Person" />
                  </motion.div>
                ) : (
                  <motion.div key="house">
                    <HouseNode />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Escrow node — visible in peer mode */}
            <div className="absolute left-1/2 top-[60%] -translate-x-1/2">
              <EscrowNode active={isPeer} />
            </div>

            {/* Floating chip — peer mode */}
            <AnimatePresence>
              {isPeer && (
                <motion.div
                  key="chip-fee"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3 }}
                  className="absolute left-1/2 top-[6%] -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[10px] font-semibold text-[#1E88E5] bg-[#1E88E5]/10 border border-[#1E88E5]/40 whitespace-nowrap"
                >
                  BetBond · 7% disclosed
                </motion.div>
              )}
            </AnimatePresence>

            {/* Floating chip — house mode */}
            <AnimatePresence>
              {!isPeer && (
                <motion.div
                  key="chip-margin"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3 }}
                  className="absolute left-1/2 top-[18%] -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[10px] font-semibold text-gray-300 bg-white/[0.04] border border-white/15 whitespace-nowrap"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 4px, transparent 4px 8px)",
                  }}
                >
                  Margin · hidden in odds
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Caption + structured comparison */}
          <div className="mt-4 rounded-2xl border border-white/10 bg-black/35 px-4 py-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.35 }}
              >
                <p className="text-center text-gray-300 text-sm leading-relaxed max-w-2xl mx-auto">
                  {renderCaption(CAPTIONS[mode])}
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3 border-t border-white/[0.06] pt-4">
                  {COMPARE[mode].map((row) => (
                    <div key={row.k} className="text-center px-1">
                      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-gray-500">
                        {row.k}
                      </div>
                      <div
                        className={`mt-1.5 text-xs sm:text-sm font-semibold leading-snug ${
                          isPeer ? "text-white" : "text-gray-300"
                        }`}
                      >
                        {row.v}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <p className="mt-5 text-center text-[11px] uppercase tracking-[0.22em] text-gray-600">
          Tap the toggle · compare the incentives
        </p>
      </div>
    </section>
  );
};

// Render *italic* segments without a markdown lib.
const renderCaption = (text) => {
  const parts = text.split("*");
  return parts.map((chunk, i) =>
    i % 2 === 1 ? (
      <em key={i} className="text-white not-italic font-medium">
        {chunk}
      </em>
    ) : (
      <React.Fragment key={i}>{chunk}</React.Fragment>
    )
  );
};

export default BondVsHouse;
